// Секцията „Приготвени рецепти" над продуктовите блокове на листа
// „Инвентаризация" — приготвени ястия, които чакат продажба. Един ред на
// рецепта: името с маркера „Рецепта", отдолу приглушено какво съдържа за
// написаните порции („Домати 0,630 кг · Краставици 0,450 кг · Сирене 0,150 кг";
// при празни порции — „за 1 порция: …"), полето „Порции" в колоната на
// „Намерено" и ✕ вдясно.
//
// Полето „Порции" е плоска клетка от СЪЩАТА решетка: id `stx-r{рецепта}-0`,
// `data-rk="r12"`, `data-c="0"` — стрелките, Enter и Esc работят като в
// „Намерено" (ключовете на рецептите са първи в `getRowKeys`), ↑ от първата
// рецепта води в търсачката, ↓ от последната — в първия продукт. ✕ е извън
// клавиатурния ред (tabIndex −1), както в другите листове; рецепта с порции пита.
// Под десктоп ширина — карти със същите id-та на полетата.
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  EMPTY_ENTRY_CELL_CLASS,
  FLAT_CELL_CLASS,
  LINE_GRID_CLASS,
  formatQtyWithUnit,
} from "./stocktakeWorksheetShared";
import { StocktakeBlockBand } from "./StocktakeBlockBand";
import { TruncatedText } from "../../components/list/TruncatedText";
import { SHEET_INPUT, SHEET_META_TEXT, SHEET_NAME_CELL, SHEET_NAME_TEXT, SHEET_ROW } from "../../components/list/density";
import { recipePortionsCellId, type StocktakeRecipeRow } from "./stocktakeRecipes";

export type StocktakeRecipeSectionProps = {
  rows: StocktakeRecipeRow[];
  decimalsOf: (productId: number) => number;
  unitOf: (productId: number) => string;
  onPortionsChange: (recipeId: number, value: string) => void;
  onPortionsBlur: (recipeId: number) => void;
  onRemove: (recipeId: number) => void;
};

// Маркерът „Рецепта" — същият като в изписването и в търсачката
// 16px — да не вдига двата реда на името над 34px в решетката
const RECIPE_PILL_CLASS =
  "shrink-0 rounded-full border border-brand-tint2 bg-brand-tint px-[9px] text-[11px] font-semibold leading-[14px] text-brand-deep-ink";
// Полето на картата — същият вид като „Намерено" на продуктовите карти
const CARD_FIELD_CLASS =
  "box-border w-full rounded-lg border border-shade-3 bg-surface px-2.5 py-2.5 text-right font-sheet text-base font-semibold leading-[normal] text-ink outline-none focus:border-brand focus:shadow-[0_0_0_3px_var(--color-brand-tint)]";

// Приглушеният ред „какво съдържа" (и тонът му)
const contentsOf = (
  row: StocktakeRecipeRow,
  { decimalsOf, unitOf }: Pick<StocktakeRecipeSectionProps, "decimalsOf" | "unitOf">,
  t: TFunction,
): { text: string; danger: boolean } => {
  if (row.ingredients.status === "loading") return { text: t("inventoryPage.sr6ContentsLoading"), danger: false };
  if (row.ingredients.status === "error") return { text: t("inventoryPage.sr6ContentsError"), danger: true };
  if (!row.contents.length) return { text: t("inventoryPage.sr6ContentsEmpty"), danger: true };
  const list = row.contents
    .map(
      (content) =>
        `${content.productName} ${formatQtyWithUnit(
          content.quantity,
          decimalsOf(content.productId),
          unitOf(content.productId),
        )}`,
    )
    .join(" · ");
  return { text: row.portions > 0 ? list : `${t("inventoryPage.sr6PerPortion")} ${list}`, danger: false };
};

type FieldProps = {
  row: StocktakeRecipeRow;
  className: string;
  onPortionsChange: StocktakeRecipeSectionProps["onPortionsChange"];
  onPortionsBlur: StocktakeRecipeSectionProps["onPortionsBlur"];
};

// Полето „Порции" — плоска клетка на решетката (и поле на картата)
const PortionsField = ({ row, className, onPortionsChange, onPortionsBlur }: FieldProps) => {
  const { t } = useTranslation();
  const recipeId = row.entry.recipeId;
  return (
    <input
      id={recipePortionsCellId(recipeId)}
      data-rk={row.key}
      data-c={0}
      inputMode="decimal"
      enterKeyHint="next"
      aria-label={`${t("inventoryPage.portionsPh")} · ${row.entry.recipeName}`}
      placeholder={t("inventoryPage.portionsPh")}
      value={row.entry.portions}
      onChange={(e) => onPortionsChange(recipeId, e.target.value)}
      onFocus={(e) => e.target.select()}
      onBlur={() => onPortionsBlur(recipeId)}
      className={`${className} placeholder:font-normal placeholder:text-faint`}
    />
  );
};

const RemoveButton = ({ row, onRemove, large }: { row: StocktakeRecipeRow; onRemove: (id: number) => void; large?: boolean }) => {
  const { t } = useTranslation();
  const label = t("inventoryPage.sr6RemoveRecipe", { name: row.entry.recipeName });
  return (
    <button
      type="button"
      onClick={() => onRemove(row.entry.recipeId)}
      aria-label={label}
      title={label}
      tabIndex={-1}
      className={`flex shrink-0 cursor-pointer items-center justify-center border-none bg-transparent text-ghost hover:text-danger-ink ${
        large ? "h-9 w-9 text-base" : "h-7 w-7 text-sm"
      }`}
    >
      ✕
    </button>
  );
};

// Решетката (≥ 1024 px): лентата + по един ред на рецепта в колоните на листа
export const StocktakeRecipeGridRows = (props: StocktakeRecipeSectionProps) => {
  const { rows, onPortionsChange, onPortionsBlur, onRemove } = props;
  const { t } = useTranslation();
  return (
    <>
      <StocktakeBlockBand kind="recipes" count={rows.length} total={rows.length} />
      {rows.map((row) => {
        const contents = contentsOf(row, props, t);
        const filled = row.portions > 0;
        return (
          <div
            key={row.key}
            className={`${LINE_GRID_CLASS} ${SHEET_ROW} items-stretch border-b border-shade-2 ${filled ? "bg-panel" : "bg-surface"}`}
          >
            {/* # — рецептите не се номерират заедно с продуктите */}
            <div className="border-r border-shade-2 bg-fill" />
            {/* РЕЦЕПТА + какво съдържа (заема и колоната „Търсено") */}
            <div className={`col-span-2 ${SHEET_NAME_CELL} border-r border-shade-2`}>
              <div className="flex min-w-0 items-center gap-2">
                <TruncatedText text={row.entry.recipeName} className={SHEET_NAME_TEXT} />
                <span className={RECIPE_PILL_CLASS}>{t("inventoryPage.recipe")}</span>
              </div>
              <TruncatedText
                text={contents.text}
                className={`${SHEET_META_TEXT} ${contents.danger ? "text-danger-ink" : "text-muted"}`}
              />
            </div>
            {/* ПОРЦИИ — в колоната на „Намерено" (↑/↓ вървят по нея) */}
            <div className="flex items-stretch gap-1 border-r border-shade-2 p-[2px] pr-2.5">
              <PortionsField
                row={row}
                onPortionsChange={onPortionsChange}
                onPortionsBlur={onPortionsBlur}
                className={`${
                  row.entry.portions === "" ? EMPTY_ENTRY_CELL_CLASS : FLAT_CELL_CLASS
                } ${SHEET_INPUT} min-w-0 flex-1 px-[9px] text-right font-sheet text-[13px] font-semibold text-ink`}
              />
              <span className="shrink-0 self-center text-[11.5px] leading-[normal] text-muted">
                {t("inventoryPage.sr6PortionsUnit")}
              </span>
            </div>
            {/* Разлика · Ед. цена · Стойност — у рецептата няма; ✕ вдясно */}
            <div className="col-span-3 flex items-center justify-end px-1.5">
              <RemoveButton row={row} onRemove={onRemove} />
            </div>
          </div>
        );
      })}
    </>
  );
};

// Картите (под 1024 px): лентата + карта на рецепта със същото id на полето
export const StocktakeRecipeCards = (props: StocktakeRecipeSectionProps) => {
  const { rows, onPortionsChange, onPortionsBlur, onRemove } = props;
  const { t } = useTranslation();
  return (
    <>
      <StocktakeBlockBand kind="recipes" count={rows.length} total={rows.length} />
      {rows.map((row) => {
        const contents = contentsOf(row, props, t);
        return (
          <div key={row.key} className="flex flex-col gap-2.5 border-t border-shade-1 px-3.5 py-3">
            <div className="flex items-start gap-2">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex items-center gap-2 font-semibold text-ink">
                  <span className="min-w-0 truncate">{row.entry.recipeName}</span>
                  <span className={RECIPE_PILL_CLASS}>{t("inventoryPage.recipe")}</span>
                </span>
                <span className={`text-xs ${contents.danger ? "text-danger-ink" : "text-muted"}`}>
                  {contents.text}
                </span>
              </div>
              <RemoveButton row={row} onRemove={onRemove} large />
            </div>
            <label className="flex max-w-[200px] flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">
                {t("inventoryPage.portionsPh")}
              </span>
              <PortionsField
                row={row}
                onPortionsChange={onPortionsChange}
                onPortionsBlur={onPortionsBlur}
                className={CARD_FIELD_CLASS}
              />
            </label>
          </div>
        );
      })}
    </>
  );
};
